<template>
  <div class="km-container">
    <!-- 顶部工具栏 -->
    <div class="km-toolbar">
      <div class="km-toolbar-left">
        <span class="km-title"><i class="el-icon-collection-tag"></i> 知识点管理</span>
      </div>
      <div class="km-toolbar-right">
        <el-select
          v-model="selectedRepoId"
          placeholder="选择题库筛选"
          size="small"
          clearable
          filterable
          style="width: 200px; margin-right: 8px;"
          @change="handleRepoChange"
        >
          <el-option
            v-for="r in repoOptions"
            :key="r.id"
            :label="r.title || r.name"
            :value="r.id"
          />
        </el-select>
        <el-input
          v-model="keyword"
          placeholder="搜索知识点名称"
          size="small"
          clearable
          style="width: 180px; margin-right: 8px;"
          prefix-icon="el-icon-search"
          @input="handleSearch"
        />
        <el-button size="small" type="primary" icon="el-icon-plus" @click="handleAddRoot">新增顶级</el-button>
        <el-button size="small" type="success" icon="el-icon-upload2" @click="importDialogVisible = true">导入知识点</el-button>
        <el-button size="small" type="primary" icon="el-icon-document" @click="mdDialogVisible = true">Markdown更新</el-button>
        <el-button size="small" type="warning" icon="el-icon-delete" @click="handleClearAll">按题库清除</el-button>
        <el-button size="small" icon="el-icon-refresh" @click="loadTree" :loading="loading">刷新</el-button>
      </div>
    </div>

    <div class="km-body" v-loading="loading">
      <div v-if="!treeData.length && !loading" class="km-empty">
        <i class="el-icon-folder-opened" style="font-size: 48px; color: #c0c4cc;"></i>
        <p>暂无知识点，请先选择题库或新增</p>
      </div>
      <el-tree
        v-else
        ref="tree"
        :data="filteredTree"
        :props="{ label: 'content', children: 'children' }"
        node-key="id"
        default-expand-all
        highlight-current
        :expand-on-click-node="true"
      >
        <div slot-scope="{ node, data }" class="km-tree-node">
          <span class="km-node-label">
            <i v-if="data.children && data.children.length" class="el-icon-folder" style="color:#e6a23c; margin-right:4px;"></i>
            <i v-else class="el-icon-document" style="color:#909399; margin-right:4px;"></i>
            {{ data.content }}
            <el-tag size="mini" type="info" style="margin-left:6px;">ID:{{ data.id }}</el-tag>
          </span>
          <span class="km-node-actions">
            <el-tooltip content="新增子节点" placement="top">
              <i class="el-icon-plus action-icon" @click.stop="handleAddChild(data)"></i>
            </el-tooltip>
            <el-tooltip content="编辑" placement="top">
              <i class="el-icon-edit action-icon" @click.stop="handleEdit(data)"></i>
            </el-tooltip>
            <el-tooltip content="删除" placement="top">
              <i class="el-icon-delete action-icon action-icon-danger" @click.stop="handleDelete(data)"></i>
            </el-tooltip>
          </span>
        </div>
      </el-tree>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="480px"
      @closed="resetForm"
    >
      <el-form ref="form" :model="form" :rules="rules" label-width="90px">
        <el-form-item label="名称" prop="content">
          <el-input v-model="form.content" placeholder="请输入知识点名称" />
        </el-form-item>
        <el-form-item label="所属题库" prop="repoId">
          <el-select v-model="form.repoId" placeholder="请选择题库" clearable filterable style="width:100%;">
            <el-option v-for="r in repoOptions" :key="r.id" :label="r.title || r.name" :value="r.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="父级节点">
          <el-input :value="parentLabel" disabled />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sortOrder" :min="0" style="width: 100%;" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="form.description" type="textarea" :rows="3" placeholder="可选" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitForm">确定</el-button>
      </div>
    </el-dialog>

    <!-- 导入知识点对话框 -->
    <el-dialog title="导入知识点" :visible.sync="importDialogVisible" width="600px">
      <el-alert
        title="导入说明"
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom: 16px;">
        <div slot="default">
          <p style="margin:4px 0;">每行一个知识点，使用 <strong>-</strong> 分隔层级关系</p>
          <p style="margin:4px 0; color:#409eff;">示例：代数-幂运算-指数运算</p>
        </div>
      </el-alert>
      <el-form label-width="80px">
        <el-form-item label="目标题库">
          <el-select v-model="importRepoId" placeholder="请选择题库" clearable filterable style="width:100%;">
            <el-option v-for="r in repoOptions" :key="r.id" :label="r.title || r.name" :value="r.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="知识点">
          <el-input
            v-model="importTextarea"
            type="textarea"
            :rows="10"
            placeholder="每行一个，使用 - 分隔层级&#10;例如：&#10;代数-幂运算-指数运算&#10;几何-平面几何-三角形"
          />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="importDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="importing" @click="confirmImport">开始导入</el-button>
      </div>
    </el-dialog>

    <!-- Markdown更新知识点对话框 -->
    <el-dialog title="更新知识点 (Markdown)" :visible.sync="mdDialogVisible" width="700px">
      <el-alert
        title="功能说明"
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom: 16px;">
        <div slot="default">
          <p style="margin:4px 0;">根据题目 <strong>number</strong> 字段批量更新 <strong>knowledge</strong> 字段</p>
          <p style="margin:4px 0;">格式：<strong>题号|知识点</strong>，知识点用 <strong>-</strong> 分隔层级</p>
          <p style="margin:4px 0; color:#409eff;">示例：1|代数-幂运算-指数运算</p>
        </div>
      </el-alert>
      <el-form label-width="80px" style="margin-bottom:12px;">
        <el-form-item label="目标题库">
          <el-select v-model="mdRepoId" placeholder="请选择题库" clearable filterable style="width:100%;">
            <el-option v-for="r in repoOptions" :key="r.id" :label="r.title || r.name" :value="r.id" />
          </el-select>
        </el-form-item>
      </el-form>
      <el-tabs v-model="mdMode">
        <el-tab-pane label="上传Markdown文件" name="file">
          <el-upload
            ref="mdUpload"
            :auto-upload="false"
            :file-list="mdFileList"
            :on-change="(f, l) => { mdFileList = l.slice(-1) }"
            :on-remove="(f, l) => { mdFileList = l }"
            accept=".md,.markdown"
            drag
            :limit="1"
            style="margin-top:8px;"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
            <div slot="tip" class="el-upload__tip">只能上传 .md 或 .markdown 文件</div>
          </el-upload>
        </el-tab-pane>
        <el-tab-pane label="手动输入" name="manual">
          <el-input
            v-model="mdTextarea"
            type="textarea"
            :rows="10"
            placeholder="每行：题号|知识点路径&#10;例如：&#10;1|代数-幂运算-指数运算&#10;2|几何-三角形"
            style="margin-top:8px;"
          />
        </el-tab-pane>
      </el-tabs>
      <div slot="footer">
        <el-button @click="mdDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="mdUpdating" @click="confirmMdUpdate">开始更新</el-button>
      </div>
    </el-dialog>

    <!-- 清除知识点对话框 -->
    <el-dialog title="清除知识点" :visible.sync="clearDialogVisible" width="420px">
      <el-alert
        title="危险操作"
        type="error"
        description="此操作将删除所选题库下的全部知识点，且无法恢复！"
        :closable="false"
        show-icon
        style="margin-bottom: 16px;"
      />
      <el-form label-width="80px">
        <el-form-item label="选择题库">
          <el-select v-model="clearRepoId" placeholder="留空则清除所有题库" clearable filterable style="width:100%;">
            <el-option v-for="r in repoOptions" :key="r.id" :label="r.title || r.name" :value="r.id" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="clearDialogVisible = false">取消</el-button>
        <el-button type="danger" :loading="clearing" @click="confirmClear">确认清除</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  listGeneralKnowledgeTree,
  createGeneralKnowledge,
  clearAllGeneralKnowledge,
  importKnowledgeFromQuestions,
  batchEnsureGeneralKnowledge
} from '@/api/knowledge'
import { batchUpdateKnowledgeByNumber } from '@/api/question'
import { fetchPaging } from '@/api/repo'
import request from '@/utils/request'

export default {
  name: 'KnowledgeManagement',
  data() {
    return {
      loading: false,
      submitting: false,
      clearing: false,
      selectedRepoId: '',
      keyword: '',
      treeData: [],
      currentNode: null,
      dialogVisible: false,
      dialogMode: 'add', // 'add' | 'edit'
      parentNode: null,
      form: {
        id: null,
        content: '',
        repoId: '',
        parentId: 0,
        sortOrder: 0,
        description: ''
      },
      rules: {
        content: [{ required: true, message: '请输入知识点名称', trigger: 'blur' }],
        repoId: [{ required: true, message: '请选择所属题库', trigger: 'change' }]
      },
      repoOptions: [],
      clearDialogVisible: false,
      clearRepoId: '',
      importDialogVisible: false,
      importRepoId: '',
      importTextarea: '',
      importing: false,
      mdDialogVisible: false,
      mdRepoId: '',
      mdMode: 'file',
      mdFileList: [],
      mdTextarea: '',
      mdUpdating: false
    }
  },
  computed: {
    dialogTitle() {
      if (this.dialogMode === 'edit') return '编辑知识点'
      return this.parentNode ? `新增子节点 —— ${this.parentNode.content}` : '新增顶级知识点'
    },
    parentLabel() {
      if (!this.parentNode) return '顶级（无父节点）'
      return `${this.parentNode.content}（ID: ${this.parentNode.id}）`
    },
    filteredTree() {
      const kw = (this.keyword || '').trim().toLowerCase()
      if (!kw) return this.treeData
      const filterNodes = (nodes) => {
        const result = []
        for (const node of nodes) {
          if ((node.content || '').toLowerCase().includes(kw)) {
            result.push(node)
          } else if (node.children && node.children.length) {
            const matched = filterNodes(node.children)
            if (matched.length) result.push({ ...node, children: matched })
          }
        }
        return result
      }
      return filterNodes(this.treeData)
    }
  },
  created() {
    this.loadTree()
    this.loadRepoOptions()
  },
  methods: {
    async loadRepoOptions() {
      try {
        const res = await fetchPaging({ pageNum: 1, pageSize: 500 })
        this.repoOptions = Array.isArray(res?.data) ? res.data : []
      } catch (e) {
        console.warn('获取题库列表失败:', e)
      }
    },
    async loadTree() {
      this.loading = true
      try {
        const res = await listGeneralKnowledgeTree(this.selectedRepoId || undefined)
        this.treeData = Array.isArray(res?.data) ? res.data : []
        this.currentNode = null
      } catch (e) {
        this.$message.error('加载知识点失败：' + (e.message || '未知错误'))
      } finally {
        this.loading = false
      }
    },
    handleRepoChange() {
      this.currentNode = null
      this.loadTree()
    },
    handleSearch() {
      // computed filteredTree 自动响应
    },
    handleNodeClick(data) {
      this.currentNode = data
      this.$refs.tree && this.$refs.tree.setCurrentKey(data.id)
    },
    handleAddRoot() {
      this.dialogMode = 'add'
      this.parentNode = null
      this.form = {
        id: null,
        content: '',
        repoId: this.selectedRepoId || '',
        parentId: 0,
        sortOrder: 0,
        description: ''
      }
      this.dialogVisible = true
    },
    handleAddChild(node) {
      this.dialogMode = 'add'
      this.parentNode = node
      this.form = {
        id: null,
        content: '',
        repoId: node.repoId || this.selectedRepoId || '',
        parentId: node.id,
        sortOrder: 0,
        description: ''
      }
      this.dialogVisible = true
    },
    handleEdit(node) {
      this.dialogMode = 'edit'
      this.parentNode = null
      this.form = {
        id: node.id,
        content: node.content,
        repoId: node.repoId || '',
        parentId: node.parentId || 0,
        sortOrder: node.sortOrder || 0,
        description: node.description || ''
      }
      this.dialogVisible = true
    },
    async submitForm() {
      const valid = await this.$refs.form.validate().catch(() => false)
      if (!valid) return
      this.submitting = true
      try {
        if (this.dialogMode === 'add') {
          await createGeneralKnowledge({
            content: this.form.content,
            repoId: this.form.repoId,
            parentId: this.form.parentId || 0,
            sortOrder: this.form.sortOrder,
            description: this.form.description
          })
          this.$message.success('新增成功')
        } else {
          await request({
            url: `/general-knowledge/${this.form.id}`,
            method: 'put',
            data: {
              content: this.form.content,
              repoId: this.form.repoId,
              parentId: this.form.parentId || 0,
              sortOrder: this.form.sortOrder,
              description: this.form.description
            }
          })
          this.$message.success('更新成功')
        }
        this.dialogVisible = false
        await this.loadTree()
      } catch (e) {
        this.$message.error('操作失败：' + (e.message || '未知错误'))
      } finally {
        this.submitting = false
      }
    },
    async handleDelete(node) {
      const hasChildren = node.children && node.children.length > 0
      const tip = hasChildren
        ? `"${node.content}" 含 ${node.children.length} 个子节点，确定要删除吗？（后端有子节点时会拒绝）`
        : `确定删除知识点"${node.content}"吗？`
      await this.$confirm(tip, '删除确认', { type: 'warning' }).catch(() => { throw new Error('cancel') })
      try {
        const res = await request({ url: `/general-knowledge/${node.id}`, method: 'delete' })
        if (res?.code === 1 || res?.code === 200) {
          this.$message.success('删除成功')
          if (this.currentNode?.id === node.id) this.currentNode = null
          await this.loadTree()
        } else {
          this.$message.error(res?.msg || '删除失败')
        }
      } catch (e) {
        if (e.message !== 'cancel') this.$message.error('删除失败：' + (e.message || '未知错误'))
      }
    },
    handleClearAll() {
      this.clearRepoId = this.selectedRepoId || ''
      this.clearDialogVisible = true
    },
    async confirmClear() {
      this.clearing = true
      try {
        const res = await clearAllGeneralKnowledge(this.clearRepoId || undefined, null)
        if (res?.code === 1 || res?.code === 200) {
          const deleted = res?.data?.deleted ?? 0
          this.$message.success(`已清除 ${deleted} 个知识点`)
          this.clearDialogVisible = false
          await this.loadTree()
        } else {
          this.$message.error(res?.msg || '清除失败')
        }
      } catch (e) {
        this.$message.error('清除失败：' + (e.message || '未知错误'))
      } finally {
        this.clearing = false
      }
    },
    resetForm() {
      this.$refs.form && this.$refs.form.resetFields()
      this.parentNode = null
    },

    async confirmImport() {
      if (!this.importTextarea.trim()) {
        this.$message.warning('请输入知识点内容')
        return
      }
      const knowledges = this.importTextarea.split('\n').map(s => s.trim()).filter(Boolean)
      if (!knowledges.length) { this.$message.warning('没有有效数据'); return }
      this.importing = true
      const loading = this.$loading({ lock: true, text: `正在导入 ${knowledges.length} 个知识点...`, background: 'rgba(0,0,0,0.7)' })
      try {
        const res = await importKnowledgeFromQuestions(knowledges, this.importRepoId || undefined, null)
        loading.close()
        if (res?.code === 1) {
          const d = res.data || {}
          this.$message.success(`导入完成：处理 ${d.processed || 0} 个，新建 ${d.created || 0} 个`)
          this.importDialogVisible = false
          this.importTextarea = ''
          await this.loadTree()
        } else {
          this.$message.error(res?.msg || '导入失败')
        }
      } catch (e) {
        loading.close()
        this.$message.error('导入失败：' + (e.message || '未知错误'))
      } finally {
        this.importing = false
      }
    },

    async confirmMdUpdate() {
      if (!this.mdRepoId) { this.$message.warning('请先选择题库'); return }
      let mappings = []
      const hierarchies = new Set()

      if (this.mdMode === 'file') {
        if (!this.mdFileList.length) { this.$message.warning('请先选择Markdown文件'); return }
        const loading = this.$loading({ lock: true, text: '正在解析Markdown文件...', background: 'rgba(0,0,0,0.7)' })
        try {
          const formData = new FormData()
          formData.append('file', this.mdFileList[0].raw)
          const response = await fetch('http://123.56.176.195:7777/parse_md_unified/', { method: 'POST', body: formData })
          if (!response.ok) throw new Error(`解析API失败: ${response.status}`)
          const result = await response.json()
          loading.close()
          if (result.status !== 'success' || !result.problems?.length) {
            this.$message.error('文件解析失败或没有找到题目'); return
          }
          for (const p of result.problems) {
            const questionId = p.question_id
            const tags = p.question_tags
            if (questionId && tags) {
              mappings.push({ number: String(questionId), knowledge: tags })
              tags.split(/[；;]/).map(s => s.trim()).filter(Boolean).forEach(k => hierarchies.add(k))
            }
          }
          if (mappings.length === 0) {
            this.$message.warning('没有找到有效的题号和知识点数据'); return
          }
        } catch (e) {
          loading.close()
          this.$message.error('解析失败：' + (e.message || '未知错误')); return
        }
      } else {
        if (!this.mdTextarea.trim()) { this.$message.warning('请输入内容'); return }
        for (const line of this.mdTextarea.split('\n').map(s => s.trim()).filter(Boolean)) {
          const idx = line.indexOf('|')
          if (idx < 0) continue
          const number = line.substring(0, idx).trim()
          const knowledge = line.substring(idx + 1).trim()
          if (number && knowledge) {
            mappings.push({ number, knowledge })
            knowledge.split(/[；;]/).map(s => s.trim()).filter(Boolean).forEach(p => hierarchies.add(p))
          }
        }
      }

      if (!mappings.length) { this.$message.warning('没有解析到有效数据'); return }

      this.mdUpdating = true
      const loading = this.$loading({ lock: true, text: `正在处理 ${mappings.length} 条知识点...`, background: 'rgba(0,0,0,0.7)' })
      try {
        const ensureRes = await batchEnsureGeneralKnowledge(
          null,
          { normalize: true, deduplicate: true, upsert: true },
          this.mdRepoId,
          null,
          Array.from(hierarchies)
        )
        loading.close()
        const pathToId = new Map()
        const batchData = ensureRes?.data || ensureRes || {}

        if (batchData.parents) {
          Object.entries(batchData.parents).forEach(([content, info]) => {
            if (info && info.id) pathToId.set(content, info.id)
          })
        }
        if (batchData.children) {
          Object.entries(batchData.children).forEach(([path, info]) => {
            if (info && info.id) pathToId.set(path, info.id)
          })
        }
        console.log('[KM] pathToId size:', pathToId.size, Array.from(pathToId.entries()).slice(0, 5))

        const updateMappings = []
        const errors = []
        for (const { number, knowledge } of mappings) {
          const allPaths = knowledge.split(/[；;]/).map(s => s.trim()).filter(Boolean)
          const ids = []; const seenIds = new Set()
          for (const onePath of allPaths) {
            const levels = onePath.split('-').map(s => s.trim()).filter(Boolean)
            let pathSoFar = ''
            for (const level of levels) {
              pathSoFar = pathSoFar ? `${pathSoFar}-${level}` : level
              const id = pathToId.get(pathSoFar)
              if (id && !seenIds.has(id)) { ids.push(id); seenIds.add(id) }
            }
          }
          if (!ids.length) { errors.push(`题号 ${number}: 找不到知识点ID`); continue }
          updateMappings.push({ number, knowledgeId: ids[ids.length - 1], knowledgeIds: ids.join(',') })
        }

        if (updateMappings.length) {
          const updateLoading = this.$loading({ lock: true, text: `正在更新 ${updateMappings.length} 条题目知识点...`, background: 'rgba(0,0,0,0.7)' })
          const updateRes = await batchUpdateKnowledgeByNumber(updateMappings, this.mdRepoId)
          updateLoading.close()
          const s = updateRes?.data
          const msg = `更新完成：成功 ${s?.successCount ?? updateMappings.length} 条` + (errors.length ? `，${errors.length} 条解析失败` : '')
          errors.length ? this.$message.warning(msg) : this.$message.success(msg)
        } else {
          this.$message.error('没有可更新的数据，请检查知识点路径')
        }

        this.mdDialogVisible = false
        this.mdTextarea = ''
        this.mdFileList = []
        await this.loadTree()
      } catch (e) {
        loading.close()
        this.$message.error('更新失败：' + (e.message || '未知错误'))
      } finally {
        this.mdUpdating = false
      }
    }
  }
}
</script>

<style scoped>
.km-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 84px);
  background: #f0f2f5;
  padding: 16px;
  box-sizing: border-box;
  gap: 12px;
}

.km-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  background: #fff;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  flex-shrink: 0;
}

.km-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
  white-space: nowrap;
}

.km-toolbar-right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.km-body {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  overflow-y: auto;
  padding: 16px;
}

.km-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #909399;
  gap: 12px;
}

.km-tree-node {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-right: 8px;
}

.km-node-label {
  display: flex;
  align-items: center;
  flex: 1;
  font-size: 13px;
}

.km-node-actions {
  display: none;
  gap: 8px;
  align-items: center;
}

.km-tree-node:hover .km-node-actions {
  display: flex;
}

.action-icon {
  font-size: 14px;
  color: #409eff;
  cursor: pointer;
  padding: 2px;
  border-radius: 3px;
  transition: background 0.2s;
}

.action-icon:hover {
  background: #ecf5ff;
}

.action-icon-danger {
  color: #f56c6c;
}

.action-icon-danger:hover {
  background: #fef0f0;
}

</style>
